import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { inject, Injectable, signal } from '@angular/core';
import {
  EMAIL_PNG_PREVIEW_DIALOG_PANEL_CLASS,
  EmailPngPreviewDialogComponent,
} from '../components/email-png-preview-dialog/email-png-preview-dialog.component';
import { EmailPngPreviewDialogData, EmailPreviewSource } from '../email-app.interfaces';
import { EmailOcrPngFile } from './email-ocr.service';

@Injectable({
  providedIn: 'root',
})
export class EmailPreviewService {
  private dialog = inject(Dialog);
  private overlay = inject(Overlay);

  // TODO: Make a generic service for previewing PNG files across different modules

  // Signals for PNG preview state
  private _showPngPreview = signal(false);
  private _selectedPngFiles = signal<EmailOcrPngFile[]>([]);
  private _previewSource = signal<EmailPreviewSource | null>(null);

  private _showPngPreviewNewApp = signal(false);
  private _selectedPngFilesNewApp = signal<EmailOcrPngFile[]>([]);

  // Public readonly signals
  readonly showPngPreview = this._showPngPreview.asReadonly();
  readonly selectedPngFiles = this._selectedPngFiles.asReadonly();
  readonly previewSource = this._previewSource.asReadonly();

  readonly showPngPreviewNewApp = this._showPngPreviewNewApp.asReadonly();
  readonly selectedPngFilesNewApp = this._selectedPngFilesNewApp.asReadonly();

  /**
   * Open PNG preview with files
   */
  openPngPreview(pngFiles: EmailOcrPngFile[]): void {
    this._selectedPngFiles.set(pngFiles);
    this._showPngPreview.set(true);
  }

  setPreviewSource(source: EmailPreviewSource): void {
    this._previewSource.set(source);
  }

  /**
   * Open PNG files in the full-screen viewer. With a source, the viewer can step through
   * the other PDFs of the same message.
   */
  openPngPreviewFullScreen(
    pngFiles: EmailOcrPngFile[],
    source: EmailPreviewSource | null
  ): DialogRef<void, EmailPngPreviewDialogComponent> {
    return this.dialog.open<void, EmailPngPreviewDialogData, EmailPngPreviewDialogComponent>(
      EmailPngPreviewDialogComponent,
      {
        panelClass: ['oiq-ui-dialog', EMAIL_PNG_PREVIEW_DIALOG_PANEL_CLASS],
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
        scrollStrategy: this.overlay.scrollStrategies.block(),
        autoFocus: false,
        data: { pngFiles, source },
      }
    );
  }

  openPngPreviewNewApp(pngFiles: EmailOcrPngFile[]): void {
    this._selectedPngFilesNewApp.set(pngFiles);
    this._showPngPreviewNewApp.set(true);
  }

  /**
   * Close PNG preview
   */
  closePngPreview(): void {
    this._showPngPreview.set(false);
    this._selectedPngFiles.set([]);
    this._previewSource.set(null);
  }

  closePngPreviewNewApp(): void {
    this._showPngPreviewNewApp.set(false);
    this._selectedPngFilesNewApp.set([]);
  }

  /**
   * Toggle PNG preview visibility
   */
  togglePngPreview(): void {
    if (this._showPngPreview()) {
      this.closePngPreview();
    } else if (this._selectedPngFiles().length > 0) {
      this._showPngPreview.set(true);
    }
  }

  /**
   * Update PNG files without changing visibility
   */
  updatePngFiles(pngFiles: EmailOcrPngFile[]): void {
    this._selectedPngFiles.set(pngFiles);
    if (pngFiles.length === 0) {
      this._showPngPreview.set(false);
    }
  }
}
